import { isEqual } from 'lodash'
import { getListDataFunctionFactory } from './getListDataFunctionFactory'
import type { TEntityNote } from '~/types/note/models'
import type { TEntityId } from '~/types/models'

const mn = '[api/msw/modules/noteMockDataFactory.ts]'

export function makeNoteMockData<T extends TEntityNote>({
  entityName,
  storageKeyPrefix,
  getStartingListData = async () => [] as unknown as T[],
}: {
  entityName: string
  storageKeyPrefix: string
  /**
   * Seed rows for this collection. Optional, and empty by default, which is what
   * every collection wanted until Cases: notes are created by the user, so
   * starting empty was the whole story.
   *
   * A case is different -- its notes also arrive from the email pipeline, and the
   * cases that matter (an ingested inbound body, a reply whose delivery
   * BOUNCED) cannot be produced by clicking. Without seed rows the mock is
   * narrower than the server and that behaviour is untestable, which is a
   * failure mode this project has paid for repeatedly.
   */
  getStartingListData?: () => Promise<T[]>
}) {
  const noteListData = undefined as unknown as T[]

  const getNoteListData = getListDataFunctionFactory<T[]>({
    listData: noteListData,
    storageKey: `${storageKeyPrefix}notes`,
    getStartingListData,
  })

  async function getNoteDataById(id: TEntityId): Promise<T> {
    const fn = `${mn} getNoteDataById |`
    const list = await getNoteListData()
    if (!list) {
      throw new Error(`${fn} returned undefined/null`)
    }
    const n = list.find((note: T) => note.id === id)
    if (!n) {
      throw new Error(`${fn} Can't find ${entityName}Note '${id}'`)
    }
    return n
  }

  async function getNoteListDataByParentId(id: TEntityId) {
    const fn = `${mn} getNoteListDataByParentId |`
    const list: T[] = await getNoteListData()
    if (!list) {
      throw new Error(`${fn} (${entityName}Notes) returned undefined/null`)
    }
    const filteredList = list.filter((note: TEntityNote) => {
      console.debug(`${fn} ${entityName}, ${note[entityName].id}, ${id}`)
      return isEqual(note[entityName].id, id)
    })
    return filteredList?.length > 0 ? filteredList : []
  }

  return {
    getNoteListData,
    getNoteDataById,
    getNoteListDataByParentId,
  }
}
